'use client';

/**
 * Utilities for storing and retrieving user information from localStorage
 */

export interface StoredUserInfo {
  name: string;
  email: string;
  storedAt: string; // ISO timestamp
}

const STORAGE_KEY_PREFIX = 'wtc_support_chat_user_';

/**
 * Get the storage key for user info based on cookie storage config
 */
function getStorageKey(cookieName?: string): string {
  if (cookieName) {
    // Use a portion of the cookie name to create a unique key
    const sanitized = cookieName.replace(/[^a-zA-Z0-9]/g, '_');
    return `${STORAGE_KEY_PREFIX}${sanitized}`;
  }
  return `${STORAGE_KEY_PREFIX}default`;
}

/**
 * Retrieves stored user info from localStorage
 */
export function getStoredUserInfo(cookieName?: string): StoredUserInfo | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const key = getStorageKey(cookieName);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as StoredUserInfo;
    
    // Validate structure
    if (typeof parsed.name === 'string' && typeof parsed.email === 'string') {
      return parsed;
    }

    return null;
  } catch (error) {
    console.error('Failed to retrieve stored user info:', error);
    return null;
  }
}

/**
 * Stores user info in localStorage
 */
export function storeUserInfo(
  name: string,
  email: string,
  cookieName?: string
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const key = getStorageKey(cookieName);
    const userInfo: StoredUserInfo = {
      name: name.trim(),
      email: email.trim(),
      storedAt: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(userInfo));
  } catch (error) {
    console.error('Failed to store user info:', error);
  }
}

/**
 * Clears stored user info from localStorage
 */
export function clearStoredUserInfo(cookieName?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const key = getStorageKey(cookieName);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear stored user info:', error);
  }
}

